import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as MealIcon } from "../../../../../assects/svg/Insight/meal_icon.svg";
import { ReactComponent as WaterIcon } from "../../../../../assects/svg/Insight/water_icon.svg";
import { ReactComponent as SleepIcon } from "../../../../../assects/svg/Insight/sleep_icon.svg";
import { ReactComponent as GutIcon } from "../../../../../assects/svg/Insight/Gut_icon.svg";

interface MonthlyLogConsistencyProps {
  consistency?: number;
  lastMonthConsistency?: number;
  meals?: number;
  water?: number;
  sleep?: number;
  gut?: number;
}

const useSpeedometerSize = () => {
  const getSize = () => {
    const w = window.innerWidth;
    if (w >= 1440) return { W: 210, H: 160 };
    if (w >= 1280) return { W: 185, H: 140 };
    return { W: 165, H: 125 };
  };

  const [size, setSize] = useState(getSize);
  useEffect(() => {
    const handleResize = () => setSize(getSize());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return size;
};

const Speedometer: React.FC<{ value: number }> = ({ value }) => {
  const [animValue, setAnimValue] = useState(0);
  const rafRef = useRef<number>(0);
  const { W, H } = useSpeedometerSize();

  useEffect(() => {
    let startTime: number | null = null;
    const phase1 = 1200;
    const phase2 = 800;
    const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const phase2Fn = (now: number) => {
      if (!startTime) startTime = now;
      const t = Math.min((now - startTime) / phase2, 1);
      setAnimValue(+(100 - easeOut(t) * (100 - value)).toFixed(2));
      if (t < 1) rafRef.current = requestAnimationFrame(phase2Fn);
      else setAnimValue(value);
    };

    const phase1Fn = (now: number) => {
      if (!startTime) startTime = now;
      const t = Math.min((now - startTime) / phase1, 1);
      setAnimValue(+(easeInOut(t) * 100).toFixed(2));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(phase1Fn);
      } else {
        setAnimValue(100);
        startTime = null;
        rafRef.current = requestAnimationFrame(phase2Fn);
      }
    };

    const tid = setTimeout(() => {
      rafRef.current = requestAnimationFrame(phase1Fn);
    }, 150);

    return () => {
      clearTimeout(tid);
      cancelAnimationFrame(rafRef.current);
    };
  }, [value]);

  const scale = W / 190;
  const cx = W / 2;
  const cy = H / 2 + 16 * scale;
  const R = 70 * scale;
  const strokeW = 20 * scale;
  const startDeg = 145;
  const sweepDeg = 250;
  const toRad = (d: number) => (d * Math.PI) / 180;

  const arcStart = toRad(startDeg);
  const arcEnd = toRad(startDeg + sweepDeg);
  const bx1 = cx + R * Math.cos(arcStart);
  const by1 = cy + R * Math.sin(arcStart);
  const bx2 = cx + R * Math.cos(arcEnd);
  const by2 = cy + R * Math.sin(arcEnd);
  const bgPath = `M ${bx1} ${by1} A ${R} ${R} 0 1 1 ${bx2} ${by2}`;

  const arcLength = R * toRad(sweepDeg);
  const visualValue = animValue > 0 ? animValue : 0.08;
  const dashArray = arcLength;
  const dashOffset = arcLength - (visualValue / 100) * arcLength;

  const needleDeg = startDeg + (visualValue / 100) * sweepDeg;
  const needleRad = toRad(needleDeg);
  const nLen = R * 0.72;
  const nTailLen = R * 0.22;
  const perpRad = needleRad + Math.PI / 2;
  const bHalf = 4.5 * scale;

  const tipX = cx + Math.cos(needleRad) * nLen;
  const tipY = cy + Math.sin(needleRad) * nLen;
  const n1x = cx + Math.cos(perpRad) * bHalf - Math.cos(needleRad) * nTailLen;
  const n1y = cy + Math.sin(perpRad) * bHalf - Math.sin(needleRad) * nTailLen;
  const n2x = cx - Math.cos(perpRad) * bHalf - Math.cos(needleRad) * nTailLen;
  const n2y = cy - Math.sin(perpRad) * bHalf - Math.sin(needleRad) * nTailLen;

  const fontSize = {
    value: 26 * scale,
    percent: 13 * scale,
    label: 10 * scale,
  };

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
      <defs>
        <filter id="nshadow-m">
          <feDropShadow dx="1" dy="2" stdDeviation="2.5" floodColor="rgba(0,0,0,0.18)" />
        </filter>
      </defs>
      <path d={bgPath} fill="none" stroke="#ECECEC" strokeWidth={strokeW} strokeLinecap="round" />
      <path
        d={bgPath}
        fill="none"
        stroke="#6DD0CF"
        strokeWidth={strokeW}
        strokeLinecap="round"
        strokeDasharray={dashArray}
        strokeDashoffset={dashOffset}
      />
      <polygon
        points={`${tipX},${tipY} ${n1x},${n1y} ${n2x},${n2y}`}
        fill="#2D2D2D"
        filter="url(#nshadow-m)"
      />
      <circle cx={cx} cy={cy} r={10 * scale} fill="#E0E0E0" />
      <circle cx={cx} cy={cy} r={6 * scale} fill="#2D2D2D" />
      <text
        x={cx}
        y={cy + R * 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={fontSize.value}
        fontWeight={700}
        fontFamily="Inter, sans-serif"
        fill="#1F1F1F"
      >
        {Math.round(animValue)}
        <tspan fontSize={fontSize.percent} fontWeight={400} fill="#8E8E93" dy={-5 * scale}>
          %
        </tspan>
      </text>
      <text
        x={cx}
        y={cy + R * 0.5 + 20 * scale}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={fontSize.label}
        fontFamily="Inter, sans-serif"
        fill="#8E8E93"
      >
        Consistency
      </text>
    </svg>
  );
};

const MonthlyLogConsistency: React.FC<MonthlyLogConsistencyProps> = ({
  consistency = 68,
  lastMonthConsistency = 40,
  meals = 80,
  water = 50,
  sleep = 100,
  gut = 20,
}) => {
  const diff = consistency - lastMonthConsistency;
  const isUp = diff >= 0;

  const metrics = [
    { label: "Meals", value: meals, Icon: MealIcon },
    { label: "Water", value: water, Icon: WaterIcon },
    { label: "Sleep", value: sleep, Icon: SleepIcon },
    { label: "Gut", value: gut, Icon: GutIcon },
  ];

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl px-5 pt-4 pb-3 flex flex-col gap-1 h-full">
      <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
        Log Consistency
      </span>

      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">
          <Speedometer value={consistency} />
        </div>

        <div className="flex flex-col gap-2 flex-1">
          <div className="flex flex-col mb-2">
            <span className="text-[14px] 3xl:text-[16px] text-[#8D8D8D] font-inter leading-tight">
              Previous month
            </span>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-[14px] 3xl:text-[16px] font-semibold text-[#1F1F1F] font-inter leading-tight">
                {lastMonthConsistency}%
              </span>
              <span
                className={`text-[12px] 3xl:text-[14px] font-medium ${
                  isUp ? "text-[#0DB5B5]" : "text-[#FF4D4D]"
                }`}
              >
                {isUp ? "↑" : "↓"}
                {Math.abs(diff).toFixed(2)}%
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {metrics.map((m, index) => (
              <div
                key={m.label}
                className={`flex items-center justify-between ${
                  index !== 0 ? "border-t border-[#F3F4F6] pt-1" : ""
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <m.Icon className="w-3.5 h-3.5 text-[#8D8D8D]" />
                  <span className="text-[10px] 3xl:text-[12px] text-[#8D8D8D] font-inter">
                    {m.label}
                  </span>
                </div>
                <span className="text-[12px] 3xl:text-[14px] font-medium text-[#1F1F1F] font-inter">
                  {m.value}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyLogConsistency;
